import {
  isIsoDateAfter,
  localTodayIsoDate,
  parseIsoDate,
} from "@/utils/format/isoDate";

/** Show soft guidance when this many customers are selected. */
export const CUSTOMERS_MANY_SELECTED_THRESHOLD = 5;

export const INVOICE_SEARCH_CUSTOMERS_MANY_HINT =
  "Searching many selected customers works, but leaving Customers empty is faster for broad results.";

export const BULK_EXPORT_CUSTOMERS_MANY_HINT =
  "Exporting many selected customers works, but leaving Customers empty is faster for large batches.";

export function resolveInvoiceSearchCustomersHint(
  customerIds: string[],
): string | null {
  if (customerIds.length >= CUSTOMERS_MANY_SELECTED_THRESHOLD) {
    return INVOICE_SEARCH_CUSTOMERS_MANY_HINT;
  }
  return null;
}

export function resolveBulkExportCustomersHint(
  customerIds: string[],
): string | null {
  if (customerIds.length >= CUSTOMERS_MANY_SELECTED_THRESHOLD) {
    return BULK_EXPORT_CUSTOMERS_MANY_HINT;
  }
  return null;
}

export interface InvoiceFilterValues {
  /** Empty = all customers */
  customerIds: string[];
  /** Empty = all statuses */
  statuses: string[];
  /** ISO date YYYY-MM-DD — invoice created from */
  dateFrom: string;
  /** ISO date YYYY-MM-DD — invoice created to */
  dateTo: string;
  /** Major units (e.g. "30" = USD 30.00) — blank = any */
  amountMin: string;
  amountMax: string;
  /** ISO date YYYY-MM-DD — due date from */
  dueDateFrom: string;
  /** ISO date YYYY-MM-DD — due date to */
  dueDateTo: string;
}

export function defaultThisMonthRange(): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return {
    dateFrom: `${y}-${m}-01`,
    dateTo: localTodayIsoDate(),
  };
}

export function emptyInvoiceFilters(
  defaultStatuses: string[] = [],
): InvoiceFilterValues {
  const { dateFrom, dateTo } = defaultThisMonthRange();
  return {
    customerIds: [],
    statuses: [...defaultStatuses],
    dateFrom,
    dateTo,
    amountMin: "",
    amountMax: "",
    dueDateFrom: "",
    dueDateTo: "",
  };
}

/** @deprecated use emptyInvoiceFilters() for defaults */
export const EMPTY_INVOICE_FILTERS: InvoiceFilterValues =
  emptyInvoiceFilters();

export function invoiceFiltersEqual(
  a: InvoiceFilterValues,
  b: InvoiceFilterValues,
): boolean {
  return (
    a.dateFrom === b.dateFrom &&
    a.dateTo === b.dateTo &&
    a.amountMin === b.amountMin &&
    a.amountMax === b.amountMax &&
    a.dueDateFrom === b.dueDateFrom &&
    a.dueDateTo === b.dueDateTo &&
    arrayEqual(a.customerIds, b.customerIds) &&
    arrayEqual(a.statuses, b.statuses)
  );
}

function arrayEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

export function parseInvoiceFiltersFromSearchParams(
  params: URLSearchParams,
  defaultStatuses: string[] = [],
): InvoiceFilterValues {
  const customerIds = parseListParam(params.get("customer_ids"));
  const legacyCustomer = params.get("customer_id")?.trim();
  if (customerIds.length === 0 && legacyCustomer) {
    customerIds.push(legacyCustomer);
  }

  let statuses = parseListParam(params.get("statuses"));
  const legacyStatus = params.get("status")?.trim();
  if (statuses.length === 0 && legacyStatus) {
    statuses = [legacyStatus];
  }
  if (
    statuses.length === 0 &&
    defaultStatuses.length > 0 &&
    !params.has("statuses") &&
    !params.has("status")
  ) {
    statuses = [...defaultStatuses];
  }

  let dateFrom = params.get("date_from")?.trim() ?? "";
  let dateTo = params.get("date_to")?.trim() ?? "";
  if (!params.has("date_from") && !params.has("date_to")) {
    const def = defaultThisMonthRange();
    dateFrom = def.dateFrom;
    dateTo = def.dateTo;
  }

  return {
    customerIds,
    statuses,
    dateFrom,
    dateTo,
    amountMin: params.get("amount_min")?.trim() ?? "",
    amountMax: params.get("amount_max")?.trim() ?? "",
    dueDateFrom: params.get("due_from")?.trim() ?? "",
    dueDateTo: params.get("due_to")?.trim() ?? "",
  };
}

export function serializeInvoiceFiltersToSearchParams(
  filters: InvoiceFilterValues,
  base: URLSearchParams,
): URLSearchParams {
  const p = new URLSearchParams(base);
  p.delete("customer_id");
  p.delete("status");

  if (filters.customerIds.length) {
    p.set("customer_ids", filters.customerIds.join(","));
  } else {
    p.delete("customer_ids");
  }

  if (filters.statuses.length) {
    p.set("statuses", filters.statuses.join(","));
  } else {
    p.delete("statuses");
  }

  if (filters.dateFrom) p.set("date_from", filters.dateFrom);
  else p.delete("date_from");

  if (filters.dateTo) p.set("date_to", filters.dateTo);
  else p.delete("date_to");

  if (filters.amountMin) p.set("amount_min", filters.amountMin);
  else p.delete("amount_min");

  if (filters.amountMax) p.set("amount_max", filters.amountMax);
  else p.delete("amount_max");

  if (filters.dueDateFrom) p.set("due_from", filters.dueDateFrom);
  else p.delete("due_from");

  if (filters.dueDateTo) p.set("due_to", filters.dueDateTo);
  else p.delete("due_to");

  return p;
}

function parseListParam(raw: string | null): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Stripe search supports a single status; multiple selections are filtered client-side. */
export function apiStatusFromFilters(statuses: string[]): string | undefined {
  if (statuses.length !== 1) return undefined;
  return statuses[0];
}

export function needsClientStatusFilter(statuses: string[]): boolean {
  return statuses.length > 1;
}

export function needsClientDateFilter(dateFrom: string, dateTo: string): boolean {
  return Boolean(dateFrom || dateTo);
}

export function needsClientAmountFilter(amountMin: string, amountMax: string): boolean {
  return Boolean(amountMin.trim() || amountMax.trim());
}

export function needsClientDueDateFilter(
  dueDateFrom: string,
  dueDateTo: string,
): boolean {
  return Boolean(dueDateFrom.trim() || dueDateTo.trim());
}

export function formatCustomersSummary(count: number): string {
  if (count === 0) return "All customers";
  return `${count} customer${count === 1 ? "" : "s"}`;
}

export function formatStatusesSummary(count: number): string {
  if (count === 0) return "All statuses";
  return `${count} status${count === 1 ? "" : "es"}`;
}

export function formatDateRangeChip(
  from: string,
  to: string,
  dateLabel = "Created",
): string {
  if (!from && !to) return "Any date";
  const def = defaultThisMonthRange();
  if (from === def.dateFrom && to === def.dateTo) {
    return `${dateLabel} this month`;
  }
  const fmt = (iso: string) => {
    const p = iso.split("-");
    if (p.length !== 3) return iso;
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    return `${months[parseInt(p[1], 10) - 1]} ${parseInt(p[2], 10)}`;
  };
  if (from && to) {
    const year = from.split("-")[0];
    if (isIsoDateRangeInvalid(from, to)) {
      return `Invalid range (${fmt(from)} – ${fmt(to)})`;
    }
    return `${fmt(from)} – ${fmt(to)}, ${year}`;
  }
  return `${from || "…"} – ${to || "…"}`;
}

export function formatDueDateChip(from: string, to: string): string | null {
  if (!from && !to) return null;
  return formatDateRangeChip(from, to).replace("Created this month", "Due this month");
}

export function isIsoDateRangeInvalid(from: string, to: string): boolean {
  const f = parseIsoDate(from);
  const t = parseIsoDate(to);
  if (!f || !t) return false;
  return f > t;
}

const AMOUNT_TYPING_PATTERN = /^\d+(\.\d{0,2})?$/;
const AMOUNT_COMPLETE_PATTERN = /^\d+(\.\d{1,2})?$/;

/** Both bounds set, complete numbers, and min > max. */
export function isAmountRangeInvalid(min: string, max: string): boolean {
  const minT = min.trim();
  const maxT = max.trim();
  if (!minT || !maxT) return false;
  if (
    !AMOUNT_COMPLETE_PATTERN.test(minT) ||
    !AMOUNT_COMPLETE_PATTERN.test(maxT)
  ) {
    return false;
  }
  return Number(minT) > Number(maxT);
}

/** Safe to apply amount bounds to search/export APIs. */
export function isInvoiceAmountFilterValid(min: string, max: string): boolean {
  const minT = min.trim();
  const maxT = max.trim();
  if (minT && !AMOUNT_TYPING_PATTERN.test(minT)) return false;
  if (maxT && !AMOUNT_TYPING_PATTERN.test(maxT)) return false;
  return !isAmountRangeInvalid(minT, maxT);
}

export interface ValidateInvoiceFilterOptions {
  /** Label for created-date fields (default "Invoice created") */
  createdDateLabel?: string;
  /** When false, skip due-date validation */
  includeDueDate?: boolean;
}

/** User-facing messages for invalid filter combinations before export/search. */
export function validateInvoiceFilterValues(
  filters: InvoiceFilterValues,
  options: ValidateInvoiceFilterOptions = {},
): string[] {
  const createdLabel = options.createdDateLabel ?? "Invoice created";
  const includeDueDate = options.includeDueDate !== false;
  const messages: string[] = [];

  if (filters.dateFrom && parseIsoDate(filters.dateFrom) === null) {
    messages.push(`${createdLabel} start date is not valid.`);
  }
  if (filters.dateTo && parseIsoDate(filters.dateTo) === null) {
    messages.push(`${createdLabel} end date is not valid.`);
  }
  const today = localTodayIsoDate();
  if (filters.dateFrom && isIsoDateAfter(filters.dateFrom, today)) {
    messages.push(`${createdLabel} start date cannot be in the future.`);
  }
  if (filters.dateTo && isIsoDateAfter(filters.dateTo, today)) {
    messages.push(`${createdLabel} end date cannot be in the future.`);
  }
  if (isIsoDateRangeInvalid(filters.dateFrom, filters.dateTo)) {
    messages.push(
      `${createdLabel}: start date must be on or before the end date.`,
    );
  }

  if (includeDueDate) {
    if (filters.dueDateFrom && parseIsoDate(filters.dueDateFrom) === null) {
      messages.push("Due date start is not valid.");
    }
    if (filters.dueDateTo && parseIsoDate(filters.dueDateTo) === null) {
      messages.push("Due date end is not valid.");
    }
    if (isIsoDateRangeInvalid(filters.dueDateFrom, filters.dueDateTo)) {
      messages.push("Due date: start date must be on or before the end date.");
    }
  }

  const min = filters.amountMin.trim();
  const max = filters.amountMax.trim();
  if (min && !AMOUNT_TYPING_PATTERN.test(min)) {
    messages.push("Amount minimum must be a valid number.");
  }
  if (max && !AMOUNT_TYPING_PATTERN.test(max)) {
    messages.push("Amount maximum must be a valid number.");
  }
  if (isAmountRangeInvalid(min, max)) {
    messages.push("Amount: minimum must not be greater than maximum.");
  }

  return messages;
}

export function formatAmountChip(min: string, max: string): string | null {
  const minN = min.trim();
  const maxN = max.trim();
  if (!minN && !maxN) return null;
  if (minN && maxN && isAmountRangeInvalid(minN, maxN)) {
    return `Invalid range (USD ${minN} – ${maxN})`;
  }
  if (minN && !maxN) return `≥ USD ${minN}`;
  if (!minN && maxN) return `≤ USD ${maxN}`;
  return `USD ${minN}–${maxN}`;
}
